/**
 * Validation logic for bulk response upload.
 * Validates BID_PRICE and BID_PRICE_TYPE for rows that exist in the grid.
 */

export type NormalizedBidPriceType = 'Price' | 'Spread' | 'Yield' | '';

/** Case-insensitive normalizer for BID_PRICE_TYPE. Maps any casing/alias to the backend enum display value. */
export function normalizeBidPriceType(value: string | null | undefined): NormalizedBidPriceType {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (lower === 'price' || lower === 'percentage') return 'Price';
  if (lower === 'spread') return 'Spread';
  if (lower === 'yield') return 'Yield';
  return '';
}

/** Returns true if the value is a recognised response type (Price/Spread/Yield, any case). */
export function isValidBidPriceType(value: string | null | undefined): boolean {
  return normalizeBidPriceType(value) !== '';
}

/** Numeric-only BOND_ID (Reference#), used for upload validation and submit-all filters. */
export const NUMERIC_BOND_ID_REGEX = /^[0-9]+$/;

export const RFQ_STATUS_FIRM_UNTIL_ERROR =
  'Cannot submit bid. Past due time.';

export interface UploadRowResult {
  rowIndex: number;
  BOND_ID: string;
  BID_PRICE: string;
  BID_PRICE_TYPE: string;
  hasValidationError: boolean;
  errorMessage?: string;
  existsInGrid: boolean;
}

export interface UploadValidationResult {
  totalRows: number;
  ignoredRows: number;
  errorRows: number;
  validRows: number;
  rows: UploadRowResult[];
}

/**
 * Format BID_PRICE for grid update (Spread → integer, Price/Yield → 3 decimals).
 */
export function formatBidPriceForUpdate(value: string, bidPriceType: string): string {
  const num = parseFloat((value ?? '').trim());
  if (isNaN(num)) return value;
  const type = normalizeBidPriceType(bidPriceType);
  return type === 'Spread' ? Math.round(num).toString() : num.toFixed(3);
}

function validateBidPrice(value: string, bidPriceType: string): { valid: boolean; formatted?: string; error?: string } {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return { valid: false, error: 'Response is required.' };
  }

  const num = parseFloat(trimmed);
  if (isNaN(num)) {
    return { valid: false, error: 'Invalid Response value.' };
  }

  const type = normalizeBidPriceType(bidPriceType);
  if (!isValidBidPriceType(bidPriceType)) {
    return { valid: false, error: 'Response Type must be Price, Spread, or Yield.' };
  }

  let formatted: string;
  if (type === 'Spread') {
    // For Spread, require an integer value (no decimals allowed)
    if (!Number.isInteger(num)) {
      return { valid: false, error: 'Response must be an integer for Spread.' };
    }
    formatted = num.toString();
  } else {
    formatted = num.toFixed(3);
  }

  return { valid: true, formatted };
}

function validateBidPriceType(value: string): { valid: boolean; error?: string } {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return { valid: false, error: 'Response Type is required.' };
  }
  if (!isValidBidPriceType(trimmed)) {
    return { valid: false, error: 'Response Type must be Price, Spread, or Yield.' };
  }
  return { valid: true };
}

/**
 * Node with data - compatible with ag-grid IRowNode/RowNode.
 */
export interface GridRowNodeLike {
  data?: any;
}

/**
 * Validate parsed rows against a data source (grid or backend lookup).
 * - Reference not found → ignore (existsInGrid: false)
 * - Reference found + invalid BID_PRICE or BID_PRICE_TYPE → hasValidationError: true
 * - Reference found + valid → valid row
 */
export function validateUploadRows(
  parsedRows: any[],
  getRowNode: (id: string) => GridRowNodeLike | undefined
): UploadValidationResult {
  const rows: UploadRowResult[] = [];
  let ignoredRows = 0;
  let errorRows = 0;
  let validRows = 0;

  for (const row of parsedRows) {
    const bondId = row.BOND_ID.trim();
    if (!bondId) {
      ignoredRows++;
      rows.push({
        ...row,
        hasValidationError: false,
        existsInGrid: false,
      });
      continue;
    }

    // Enforce numeric-only Reference# / BOND_ID for security and clear feedback
    if (!NUMERIC_BOND_ID_REGEX.test(bondId)) {
      errorRows++;
      rows.push({
        ...row,
        hasValidationError: true,
        errorMessage: 'Reference# must be numeric.',
        existsInGrid: false,
      });
      continue;
    }

    const node = getRowNode(bondId);
    if (!node?.data) {
      ignoredRows++;
      rows.push({
        ...row,
        hasValidationError: false,
        existsInGrid: false,
      });
      continue;
    }

    const status = (node.data?.STATUS as string) ?? '';
    if (status === 'FIRM_IN') {
      errorRows++;
      rows.push({
        ...row,
        hasValidationError: true,
        errorMessage: RFQ_STATUS_FIRM_UNTIL_ERROR,
        existsInGrid: true,
      });
      continue;
    }

    const typeValidation = validateBidPriceType(row.BID_PRICE_TYPE);
    const priceValidation = validateBidPrice(row.BID_PRICE, row.BID_PRICE_TYPE);

    if (!typeValidation.valid) {
      errorRows++;
      rows.push({
        ...row,
        hasValidationError: true,
        errorMessage: typeValidation.error,
        existsInGrid: true,
      });
      continue;
    }

    if (!priceValidation.valid) {
      errorRows++;
      rows.push({
        ...row,
        hasValidationError: true,
        errorMessage: priceValidation.error,
        existsInGrid: true,
      });
      continue;
    }

    validRows++;
    rows.push({
      ...row,
      hasValidationError: false,
      existsInGrid: true,
    });
  }

  return {
    totalRows: parsedRows.length,
    ignoredRows,
    errorRows,
    validRows,
    rows,
  };
}

